import React, { useState, useEffect } from "react";
import moment from "moment";
import Card from "../Card"; // adjust path to match your project structure

// ── Bucket config — fresh first, critical last ─────────────────────────────────
const BUCKETS = [
    {
        id: "fresh",
        label: "< 5 min",
        minSeconds: 0,
        maxSeconds: 300,
        borderColor: "border-green-400",
        headerBg: "bg-green-50",
        headerText: "text-green-800",
        labelText: "text-green-600",
        dot: "bg-green-400",
        badgeBg: "bg-green-500",
        pulse: false,
    },
    {
        id: "watch",
        label: "5 – 10 min",
        minSeconds: 300,
        maxSeconds: 600,
        borderColor: "border-yellow-400",
        headerBg: "bg-yellow-50",
        headerText: "text-yellow-800",
        labelText: "text-yellow-600",
        dot: "bg-yellow-400",
        badgeBg: "bg-yellow-500",
        pulse: false,
    },
    {
        id: "urgent",
        label: "10 – 15 min",
        minSeconds: 600,
        maxSeconds: 900,
        borderColor: "border-orange-400",
        headerBg: "bg-orange-50",
        headerText: "text-orange-800",
        labelText: "text-orange-600",
        dot: "bg-orange-400",
        badgeBg: "bg-orange-500",
        pulse: false,
    },
    {
        id: "critical",
        label: "15 min+",
        minSeconds: 900,
        maxSeconds: Infinity,
        borderColor: "border-red-500",
        headerBg: "bg-red-50",
        headerText: "text-red-800",
        labelText: "text-red-600",
        dot: "bg-red-500",
        badgeBg: "bg-red-500",
        pulse: true,
    },
];

const CARD_GRID = "grid gap-3 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5";

function getElapsedSeconds(orderTime) {
    const currentDate = new Date().toLocaleDateString("en-CA");
    const start = moment(`${currentDate} ${orderTime}`, "YYYY-MM-DD HH:mm:ss");
    return moment().diff(start, "seconds");
}

function classifyOrder(orderTime) {
    const secs = getElapsedSeconds(orderTime);
    return BUCKETS.find(b => secs >= b.minSeconds && secs < b.maxSeconds) ?? BUCKETS[0];
}

// ── BucketSection ──────────────────────────────────────────────────────────────
const BucketSection = ({ bucket, orders, handlers }) => {
    const { handleFinished, handleCompleted, handleMinus, handleCancel, handleCookProcess, speakOrder } = handlers;
    const isEmpty = orders.length === 0;

    return (
        <div className={`border-l-4 ${bucket.borderColor}`}>
            {/* Header */}
            <div className={`${bucket.headerBg} flex items-center gap-3 px-4 py-2`}>
                {/* Dot */}
                <span className={`w-2 h-2 rounded-full shrink-0 ${bucket.dot} ${bucket.pulse ? "animate-pulse" : ""}`} />

                {/* Label */}
                <span className={`text-sm font-semibold uppercase tracking-wider ${bucket.headerText}`}>
                    {bucket.label}
                </span>

                {/* Count badge */}
                <span className={`ml-1 ${bucket.badgeBg} text-white text-[10px] font-bold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center`}>
                    {orders.length}
                </span>

                {/* Divider line fills remaining space */}
                <div className="flex-1 border-t border-dashed border-gray-200 ml-2" />
            </div>

            {/* Cards grid — always rendered, blank if empty */}
            <div className={`p-3 ${isEmpty ? "min-h-[72px]" : ""}`}>
                {!isEmpty && (
                    <div className={CARD_GRID}>
                        {orders.map((element, index) => (
                            <Card
                                key={element.table_id ?? index}
                                element={element}
                                index={index}
                                handleFinished={handleFinished}
                                handleCompleted={handleCompleted}
                                handleMinus={handleMinus}
                                handleCancel={handleCancel}
                                handleCookProcess={handleCookProcess}
                                speakOrder={speakOrder}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ── TimelineView ───────────────────────────────────────────────────────────────
const TimelineView = ({ list, handlers }) => {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 10_000);
        return () => clearInterval(id);
    }, []);

    const bucketed = [...BUCKETS]
        .reverse()
        .map(bucket => ({
            bucket,
            orders: list
                .filter(o => classifyOrder(o.orderTime).id === bucket.id)
                .sort((a, b) => getElapsedSeconds(b.orderTime) - getElapsedSeconds(a.orderTime)),
        }));

    return (
        <div className="flex flex-col gap-4">
            {bucketed.map(({ bucket, orders }) => (
                <BucketSection
                    key={bucket.id}
                    bucket={bucket}
                    orders={orders}
                    handlers={handlers}
                />
            ))}
        </div>
    );
};

export default TimelineView;